import { ColumnTable } from 'arquero'
import { PinnedMorphotype, ScatterPlotMetadata } from 'redux/slices/types'

import { enhanceSelectionWithGrayCellsFromDataFrame } from './plot/enhanceSelectionWithGrayCells'

/**
 * Version configuration for morphotype format.
 * This will be integrated into the session data structure.
 */
export interface VersionConfig {
  morphotypeFormatVersion?: number
  // Other version fields may be added here
}

/**
 * Detects if a morphotype needs migration from old format to new format.
 *
 * Old format morphotypes:
 * - Have `cellInfoGroupsSnapshot` field (indicates filter tracking)
 * - Have `comparisonDimensionsSnapshot` field (indicates multi-plot mode restriction)
 * - Version config is older than new format version
 *
 * New format morphotypes:
 * - Include ALL cells in geometric region (after prefiltering only)
 * - Independent of active filters
 * - Multi-plot mode is position-only (cells from all tiles included)
 */
export function needsMigration(
  morphotype: PinnedMorphotype,
  versionConfig: VersionConfig
): boolean {
  // Check version number - if already v2 or higher, no migration needed
  if (versionConfig.morphotypeFormatVersion && versionConfig.morphotypeFormatVersion >= 2) {
    return false
  }

  // Has old filter snapshot - needs migration
  if (morphotype.cellInfoGroupsSnapshot) {
    return true
  }

  // Was created in multi-plot mode (restricted to one tile) - needs migration
  if (morphotype.comparisonDimensionsSnapshot) {
    return true
  }

  return false
}

/**
 * Recomputes morphotype cells using pure geometric selection.
 * Uses DataFrame for memory efficiency.
 *
 * Uses `enhanceSelectionWithGrayCellsFromDataFrame` infrastructure to:
 * - Extract geometric selection (lassoPoints or range) from morphotype
 * - Apply geometric point-in-polygon logic to ALL cells in DataFrame
 * - Return all cells within the geometric region
 */
export function recomputeMorphotypeGeometrically(
  morphotype: PinnedMorphotype,
  dataFrame: ColumnTable,
  prefilteredCellIdSet: Set<string>,
  scatterPlotMetaData: ScatterPlotMetadata
): PinnedMorphotype {
  // Always use cells (not transposedCells) for geometry
  const geometricSelection = morphotype.cells

  if (!geometricSelection) {
    return morphotype
  }

  // Skip migration if no geometry to recompute from
  // (shouldn't happen in practice - lassoPoints/range is always set when user draws a selection)
  const hasGeometry = geometricSelection.lassoPoints || geometricSelection.range
  if (!hasGeometry) {
    return morphotype
  }

  // Use DataFrame version of enhanceSelectionWithGrayCells
  // IMPORTANT: Clear selections to force regeneration from lassoPoints
  // This fixes where selections.path had corrupted coordinates
  const recomputedSelection = enhanceSelectionWithGrayCellsFromDataFrame({
    selection: {
      lassoPoints: geometricSelection.lassoPoints,
      range: geometricSelection.range,
      selections: undefined, // Clear corrupted selections - will be regenerated from lassoPoints
      points: [], // Empty - force recompute all
    },
    dataFrame,
    prefilteredCellIdSet,
    scatterPlotMetaData,
    visibleSelectedComparisonDimensions: undefined, // Force single-plot mode for geometric selection
    isPreFilter: false,
  })

  return {
    ...morphotype,
    cells: recomputedSelection,
    transposedCells: undefined,
    cellInfoGroupsSnapshot: undefined,
    comparisonDimensionsSnapshot: undefined,
  }
}

/**
 * Migrates all morphotypes in a list that need migration.
 * Uses DataFrame for memory efficiency.
 */
export function migrateMorphotypes(
  morphotypes: PinnedMorphotype[],
  versionConfig: VersionConfig,
  dataFrame: ColumnTable,
  prefilteredCellIdSet: Set<string>,
  scatterPlotMetaData: ScatterPlotMetadata
): {
  morphotypes: PinnedMorphotype[]
  versionConfig: VersionConfig
  migratedCount: number
  migratedMorphotypeIds: number[]
} {
  // Check if any morphotypes need migration
  const needsMigrationCheck = morphotypes.some((m) => needsMigration(m, versionConfig))

  if (!needsMigrationCheck) {
    return {
      morphotypes,
      versionConfig: {
        ...versionConfig,
        morphotypeFormatVersion: 2,
      },
      migratedCount: 0,
      migratedMorphotypeIds: [],
    }
  }

  // Perform migration
  let migratedCount = 0
  const migratedMorphotypeIds: number[] = []
  const migratedMorphotypes = morphotypes.map((m) => {
    if (needsMigration(m, versionConfig)) {
      migratedCount += 1
      migratedMorphotypeIds.push(m.id)
      return recomputeMorphotypeGeometrically(
        m,
        dataFrame,
        prefilteredCellIdSet,
        scatterPlotMetaData
      )
    }
    return m
  })

  return {
    morphotypes: migratedMorphotypes,
    versionConfig: {
      ...versionConfig,
      morphotypeFormatVersion: 2,
    },
    migratedCount,
    migratedMorphotypeIds,
  }
}
